/* eslint-disable max-lines */
import React, { Suspense, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Flex } from 'glints-aries/es';
import {
  ButtonGroup,
  PrimaryButton,
  Spinner,
  Typography,
} from 'glints-aries/es/@next';
import { Blue } from 'glints-aries/es/@next/utilities/colors';
import { space12, space16 } from 'glints-aries/es/@next/utilities/spacing';
import { compact, get, intersection } from 'lodash-es';
import { useWatch } from 'react-hook-form';
import {
  defineMessages,
  FormattedMessage,
  MessageDescriptor,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { createStateContext } from 'react-use';

import { CompanyStatus } from '../../../common/constants';
import { assertIsDefined } from '../../../common/helpers/assertIsDefined';
import useGetCompanyVerificationInfo from '../../../common/hooks/useGetCompanyVerificationInfo';
import useIsLegalDocsAutoVerifyEnabled from '../../../common/hooks/useIsLegalDocsAutoVerifyEnabled';
import {
  AutoRejectedReasons,
  CompanyVerificationMethod,
  RejectionReason,
  VerificationMethod,
} from '../../../common/interfaces/entities';
import { ROUTES } from '../../../common/routes';
import useMarketingTrackingService from '../../../common/tracking/useMarketingTrackingService';
import {
  INDONESIA_COUNTRY,
  SINGAPORE_COUNTRY,
} from '../../../modules/JobV2/common/constants';
import { getSessionCompanyCountryCode } from '../../../selectors/session';
import { useCompanyVerificationModalsContext } from '../../CompanyVerificationModals/CompanyVerificationModalsProvider';
import { ContactUsButton } from './components/ContactUsButton';
import {
  useCompanyVerificationFormContext,
  useIsViewOnlyMode,
} from './FormContext';
import FormProvider from './FormProvider';
import useCompanyVerificationSubmittedModal from './hooks/useCompanyVerificationSubmittedModal';
import { useCompanyVerificationTracking } from './hooks/useCompanyVerificationTracking';
import {
  COMPANY_VERIFICATION_POLLING_TIMEOUT_IN_MS,
  CompanyVerificationPollingMode,
  useGetCompanyVerificationInfoWithPolling,
} from './hooks/useGetCompanyVerificationInfoWithPolling';
import useGetCompanyVerificationMethods from './hooks/useGetCompanyVerificationMethods';
import useRequestCompanyVerification from './hooks/useRequestCompanyVerification';
import useSubmitButtonEnabled from './hooks/useSubmitButtonEnabled';
import { useUpdateCompanyVerificationModal } from './hooks/useUpdateCompanyVerificationModal';
import { buildLegalDocumentVerificationInput } from './legalDocumentVerificationInput';
import {
  FormContentStyles,
  PaddedContainer,
  StyledButtonGroupFooterWrapper,
  StyledLeftButtonWrapper,
  StyledLoadingModal,
  StyledModal,
  StyledRightButtonWrapper,
  TypographyWithBulletPoint,
  VerificationModalBanner,
} from './styles.sc';
import {
  CompanyVerificationFormState,
  VerifyWithProofPersonalDocumentType,
} from './types';
import { getAssetFileNameFromURL, isURL } from './utils';
import { VerificationMethodCard } from './VerificationMethodCard';
import VerifyWithLegalDocumentsForm from './VerifyWithLegalDocumentsForm';
import VerifyWithProofOfOwnershipForm from './VerifyWithProofOfOwnershipForm';

const messages = defineMessages({
  modalTitle: {
    id: 'text-choose-method-of-verification',
    defaultMessage: 'Choose Verification Method',
  },
});

const verificationMethodToText: Record<VerificationMethod, MessageDescriptor> =
  {
    [VerificationMethod.LEGAL_DOCUMENTS]: {
      id: 'text-verify-with-legal-documents',
      defaultMessage: 'Verify with Legal Documents',
    },
    [VerificationMethod.PROOF_OF_OWNERSHIP]: {
      id: 'text-verify-with-proof-of',
      defaultMessage: 'Verify with Proof of Ownership',
    },
  } as const;

const [useErrorContext, ErrorProvider] = createStateContext<ApolloError | null>(
  null
);

function convertPopularityTagToMessageDescriptor(
  verificationMethods: CompanyVerificationMethod[]
) {
  return verificationMethods.map(verificationMethod => ({
    ...verificationMethod,
    popularityTag: verificationMethod.popularityTag
      ? ({
          id: verificationMethod.popularityTag.key,
          defaultMessage: verificationMethod.popularityTag.defaultMessage,
        } as MessageDescriptor)
      : null,
  }));
}

function RenderCompanyBenefits({ formDisabled }: { formDisabled?: boolean }) {
  const { companyVerificationMethods } = useGetCompanyVerificationMethods();
  const { getValues, setValue, control } = useCompanyVerificationFormContext();
  const selectedVerificationMethod = useWatch({
    name: 'verificationMethod',
    control,
  });
  const { verificationInfo } = useGetCompanyVerificationInfo();
  const isViewOnly = useIsViewOnlyMode();
  const availableTierUpgrades =
    verificationInfo?.tierUpgrade?.availableTierUpgrade;
  const verificationMethodsFiltered = convertPopularityTagToMessageDescriptor(
    availableTierUpgrades ?? companyVerificationMethods
  ).filter(
    companyVerificationMethod =>
      !isViewOnly ||
      companyVerificationMethod.verificationMethod ===
        selectedVerificationMethod
  );

  const { trackCompanyVerificationClickEvent } =
    useCompanyVerificationTracking();
  const handleVerificationMethodClick = (
    newCertificationMethod: VerificationMethod
  ) => {
    const originalVerificationMethod = getValues('verificationMethod');

    setValue('verificationMethod', newCertificationMethod, {
      shouldValidate: true,
    });

    if (newCertificationMethod !== originalVerificationMethod) {
      trackCompanyVerificationClickEvent('verification_method_selected');
    }
  };

  return (
    <div
      style={{
        margin: '20px 0',
        display: 'grid',
        gridTemplateColumns: `repeat(${verificationMethodsFiltered.length}, minmax(0, 1fr))`,
        gap: space12,
      }}
    >
      {verificationMethodsFiltered.map(props => {
        const isActive =
          selectedVerificationMethod === props.verificationMethod;

        return (
          <VerificationMethodCard
            key={props.verificationMethod}
            {...props}
            active={isActive}
            onClick={
              isViewOnly || formDisabled
                ? undefined
                : () => {
                    handleVerificationMethodClick(props.verificationMethod);
                  }
            }
            hidePopularityTag={verificationMethodsFiltered.length === 1}
            disabled={!isActive && formDisabled}
          />
        );
      })}
    </div>
  );
}

function FooterButtons({
  onClose,
  setFormDisabled,
}: {
  onClose: () => void;
  setFormDisabled: (value: boolean) => void;
}) {
  const { requestCompanyVerificationRequest, loading } =
    useRequestCompanyVerification();
  const { handleSubmit, setValue } = useCompanyVerificationFormContext();
  const { openModal: openCompanyVerificationSubmittedModal } =
    useCompanyVerificationSubmittedModal();
  const isViewOnly = useIsViewOnlyMode();
  const isSubmitEnabled = useSubmitButtonEnabled();
  const [, setError] = useErrorContext();
  const isLegalDocsAutoVerifyEnabled = useIsLegalDocsAutoVerifyEnabled();
  const { isPollingRunning, waitForVerificationStatus } =
    useGetCompanyVerificationInfoWithPolling();
  const { openSuccessModal } = useCompanyVerificationModalsContext();
  const { openModal: openUpdateCompanyVerificationModal } =
    useUpdateCompanyVerificationModal();
  const { sendMarketingTrackingEvent } = useMarketingTrackingService();
  const companyCountryCode = useSelector(getSessionCompanyCountryCode);
  const history = useHistory();
  const { trackCompanyVerificationClickEvent } =
    useCompanyVerificationTracking();

  if (isViewOnly) {
    return (
      <ButtonGroup>
        <PrimaryButton onClick={onClose}>
          <Typography variant="button">
            <FormattedMessage id="interactive-close" defaultMessage="Close" />
          </Typography>
        </PrimaryButton>
      </ButtonGroup>
    );
  }

  function getFileName(value: string) {
    return isURL(value) ? getAssetFileNameFromURL(value) || '' : value;
  }

  const onSubmit = handleSubmit(async (data: CompanyVerificationFormState) => {
    setError(null);
    setFormDisabled(true);
    let didError = false;

    trackCompanyVerificationClickEvent('verification_submit_clicked');

    await requestCompanyVerificationRequest({
      input:
        data.verificationMethod === VerificationMethod.LEGAL_DOCUMENTS
          ? buildLegalDocumentVerificationInput(data.verifyWithLegalDocuments)
          : {
              verificationMethod: data.verificationMethod,
              proofOfOwnershipData: {
                personalNIBNumber:
                  data.verifyWithProofOfOwnership.personalNIBNumber || null,
                businessLocationType:
                  data.verifyWithProofOfOwnership.businessLocationType,
                ...(data.verifyWithProofOfOwnership.personalDocumentType ===
                VerifyWithProofPersonalDocumentType.NPWP
                  ? {
                      personalNPWPDoc: getFileName(
                        data.verifyWithProofOfOwnership.personalNPWPDoc
                      ),
                      personalNPWPNumber:
                        data.verifyWithProofOfOwnership.personalNPWPNumber,
                    }
                  : {
                      ktpDoc: getFileName(
                        data.verifyWithProofOfOwnership.ktpDoc
                      ),
                      ktpNumber: data.verifyWithProofOfOwnership.ktpNumber,
                    }),
                businessActivityImages: compact(
                  data.verifyWithProofOfOwnership.businessActivityImages
                ).map(image =>
                  isURL(image)
                    ? (getAssetFileNameFromURL(image) as string)
                    : image
                ),
                additionalDocuments: compact(
                  data.verifyWithProofOfOwnership.additionalDocuments
                ).map(image =>
                  isURL(image)
                    ? (getAssetFileNameFromURL(image) as string)
                    : image
                ),
              },
            },
      onCompleted: async ({
        lastVerificationRequest: submittedRequest,
        tierUpgrade,
      }) => {
        const isIDLegalDocumentAutoVerification =
          isLegalDocsAutoVerifyEnabled &&
          companyCountryCode === INDONESIA_COUNTRY.code &&
          data.verifyWithLegalDocuments.companyNPWPDoc &&
          data.verifyWithLegalDocuments.companyNPWPDocMetadata &&
          !data.verifyWithLegalDocuments.legalDocumentNotOfficial;
        const isSGAcraBizFileAutoVerification =
          isLegalDocsAutoVerifyEnabled &&
          companyCountryCode === SINGAPORE_COUNTRY.code &&
          data.verifyWithLegalDocuments.acraBizFile;

        trackCompanyVerificationClickEvent('company_verification_submitted');

        if (
          isIDLegalDocumentAutoVerification ||
          isSGAcraBizFileAutoVerification
        ) {
          const {
            status,
            verificationInfo: { lastVerificationRequest: polledRequest },
          } = await waitForVerificationStatus({
            mode: isSGAcraBizFileAutoVerification
              ? CompanyVerificationPollingMode.SG_ACRA_BIZFILE
              : CompanyVerificationPollingMode.ID_LEGAL_DOCUMENT,
            timeoutInMs: isSGAcraBizFileAutoVerification
              ? COMPANY_VERIFICATION_POLLING_TIMEOUT_IN_MS
              : undefined,
            fallbackValue: submittedRequest
              ? {
                  status: submittedRequest.company.status,
                  verificationInfo: {
                    isInitiated: true,
                    isRejected: false,
                    lastVerificationRequest: submittedRequest,
                    tierUpgrade,
                  },
                }
              : undefined,
          });
          const lastVerificationRequest = polledRequest ?? submittedRequest;
          assertIsDefined(lastVerificationRequest);

          switch (status) {
            case CompanyStatus.VERIFIED: {
              onClose();
              openSuccessModal({
                lastVerificationRequest,
              });
              return;
            }
            case CompanyStatus.REJECTED: {
              onClose();
              openUpdateCompanyVerificationModal({
                lastVerificationRequest,
              });
              // Allow the user to update the form again after rejection
              setFormDisabled(false);

              const hasCompanyNameMismatch = Boolean(
                lastVerificationRequest?.rejectionReasons?.includes(
                  RejectionReason.AUTO_LEGAL_DOCUMENT_COMPANY_NAME_MISMATCH
                )
              );
              // Pre-fill metadata so extracted company name and NPWP number are available as suggested values
              setValue(
                'verifyWithLegalDocuments.companyNPWPDocMetadata',
                get(
                  lastVerificationRequest,
                  'document.companyNpwpDocumentMetadata'
                )
              );
              setValue(
                'verifyWithLegalDocuments.allowCompanyNameEdit',
                hasCompanyNameMismatch
              );
              return;
            }
            case CompanyStatus.BLOCKED: {
              onClose();
              history.push(`/${ROUTES.companyReportStatus}`);
              return;
            }
            default: {
              onClose();
              openCompanyVerificationSubmittedModal({
                submittedDate: lastVerificationRequest.createdAt,
                isTierUpgrade: Boolean(tierUpgrade),
              });
              return;
            }
          }
        }

        if (submittedRequest) {
          onClose();
          openCompanyVerificationSubmittedModal({
            submittedDate: submittedRequest.createdAt,
            isTierUpgrade: Boolean(tierUpgrade),
          });
        }
      },
      onError: error => {
        didError = true;
        setError(error);
        document
          .getElementsByClassName('verification-modal-banner')[0]
          ?.scrollIntoView({
            behavior: 'smooth',
          });
      },
    });

    if (didError) {
      return;
    }

    sendMarketingTrackingEvent({
      event: 'verification_submitted',
      payload: {
        // eslint-disable-next-line camelcase
        verification_type: data.verificationMethod,
      },
    });
  });

  return (
    <StyledButtonGroupFooterWrapper>
      <StyledLeftButtonWrapper>
        <ContactUsButton />
      </StyledLeftButtonWrapper>
      <StyledRightButtonWrapper>
        <PrimaryButton
          disabled={!isSubmitEnabled}
          loading={loading || isPollingRunning}
          onClick={onSubmit}
        >
          <Typography variant="button">
            <FormattedMessage id="text-submit" defaultMessage="Submit" />
          </Typography>
        </PrimaryButton>
      </StyledRightButtonWrapper>
    </StyledButtonGroupFooterWrapper>
  );
}

function RenderFormContent({ formDisabled }: { formDisabled?: boolean }) {
  const companyCountryCode = useSelector(getSessionCompanyCountryCode);
  const selectedVerificationMethod = useWatch({
    name: 'verificationMethod',
    control: useCompanyVerificationFormContext().control,
  });

  return (
    <>
      <FormContentStyles.VisibilityContainer
        visible={
          selectedVerificationMethod === VerificationMethod.LEGAL_DOCUMENTS
        }
      >
        <Choose>
          <When condition={companyCountryCode === SINGAPORE_COUNTRY.code}>
            <VerifyWithLegalDocumentsForm.SGForm
              VerificationBanner={<VerificationBanner />}
              disabled={formDisabled}
            />
          </When>
          <When condition={companyCountryCode === INDONESIA_COUNTRY.code}>
            <VerifyWithLegalDocumentsForm.IDForm
              VerificationBanner={<VerificationBanner />}
              disabled={formDisabled}
            />
          </When>
          <Otherwise>
            <VerifyWithLegalDocumentsForm.GenericForm
              VerificationBanner={<VerificationBanner />}
              countryCode={companyCountryCode}
              disabled={formDisabled}
            />
          </Otherwise>
        </Choose>
      </FormContentStyles.VisibilityContainer>
      <FormContentStyles.VisibilityContainer
        visible={
          selectedVerificationMethod === VerificationMethod.PROOF_OF_OWNERSHIP
        }
      >
        <VerifyWithProofOfOwnershipForm
          VerificationBanner={<VerificationBanner />}
        />
      </FormContentStyles.VisibilityContainer>
    </>
  );
}
function LoadingModal() {
  const countryCode = useSelector(getSessionCompanyCountryCode);

  return (
    <StyledLoadingModal isOpen={true} countryCode={countryCode}>
      <Spinner size="large" fill={Blue.S99} />
    </StyledLoadingModal>
  );
}

function RenderRejectionReason({
  rejectionReasonText,
}: {
  rejectionReasonText: string;
}) {
  const chunks = compact(rejectionReasonText.split('\n'));

  return (
    <div>
      {chunks.map((value, index) => (
        <TypographyWithBulletPoint
          key={`rejection-reason-${index}`}
          variant="body2"
        >
          {value}
        </TypographyWithBulletPoint>
      ))}
    </div>
  );
}

function VerificationBanner() {
  const { verificationInfo } = useGetCompanyVerificationInfo();
  const [submissionError] = useErrorContext();
  const selectedVerificationMethod = useWatch({
    name: 'verificationMethod',
    control: useCompanyVerificationFormContext().control,
  });

  const isLastRequestRejected = Boolean(
    verificationInfo?.lastVerificationRequest?.rejectionReasonText
  );
  const isSameMethodAsLastVerificationRequest =
    selectedVerificationMethod ===
    verificationInfo?.lastVerificationRequest?.companyVerificationMethod
      .verificationMethod;
  if (
    !submissionError &&
    (!isLastRequestRejected || !isSameMethodAsLastVerificationRequest)
  ) {
    return null;
  }

  const isLastRequestAutoRejected =
    intersection(
      AutoRejectedReasons,
      verificationInfo?.lastVerificationRequest?.rejectionReasons
    ).length > 0;

  return (
    <VerificationModalBanner
      dismissable={false}
      status={
        verificationInfo?.isRejected ||
        verificationInfo?.tierUpgrade?.isUpgradeRejected ||
        submissionError
          ? 'critical'
          : undefined
      }
      className="verification-modal-banner"
    >
      <Choose>
        <When condition={submissionError}>
          <Typography variant="body1">
            <FormattedMessage
              id="text-something-went-wrong"
              defaultMessage="Something went wrong. Please try again later."
            />
          </Typography>
        </When>
        <When
          condition={
            verificationInfo?.lastVerificationRequest?.rejectionReasonText
          }
        >
          {verificationInfo?.lastVerificationRequest?.rejectionReasonText && (
            <Flex flexDirection="column">
              <Typography variant="body1">
                <FormattedMessage
                  id="text-we-were-unable-to-verify"
                  defaultMessage="We were unable to verify the information provided due to the following reason:"
                />
              </Typography>
              <RenderRejectionReason
                rejectionReasonText={
                  verificationInfo.lastVerificationRequest.rejectionReasonText
                }
              />
              <div style={{ marginTop: space16 }}>
                <Choose>
                  <When condition={isLastRequestAutoRejected}>
                    <Typography variant="body1">
                      <FormattedMessage
                        id="text-correct-any-discrepancies-and"
                        defaultMessage="Please resubmit your verification following the instructions. Our sales team will contact you shortly to assist, or you can reach us for support."
                        values={{ br: <br /> }}
                      />
                    </Typography>
                  </When>
                  <Otherwise>
                    <Typography variant="body1">
                      <FormattedMessage
                        id="contact-us-for-verif-support"
                        defaultMessage="Our sales team will contact you shortly to assist, or you can reach us for support."
                      />
                    </Typography>
                  </Otherwise>
                </Choose>
              </div>
            </Flex>
          )}
        </When>
      </Choose>
    </VerificationModalBanner>
  );
}

function RenderTitle() {
  const { verificationInfo } = useGetCompanyVerificationInfo();
  const verificationMethod = useWatch({
    name: 'verificationMethod',
    control: useCompanyVerificationFormContext().control,
  });
  const message = verificationInfo?.tierUpgrade
    ? verificationMethodToText[verificationMethod]
    : messages.modalTitle;

  return <FormattedMessage {...message} />;
}

function ModalComponent() {
  const countryCode = useSelector(getSessionCompanyCountryCode);
  const { closeVerificationModal } = useCompanyVerificationModalsContext();
  const [formDisabled, setFormDisabled] = useState(false);

  return (
    <FormProvider>
      <ErrorProvider>
        <StyledModal
          isOpen={true}
          customActions={
            <FooterButtons
              onClose={closeVerificationModal}
              setFormDisabled={setFormDisabled}
            />
          }
          onClose={closeVerificationModal}
          closeOnClickOutside={true}
          showHeaderBorder={false}
          header={<RenderTitle />}
          countryCode={countryCode}
          zIndexOverride={1003}
        >
          <PaddedContainer>
            <RenderCompanyBenefits formDisabled={formDisabled} />
          </PaddedContainer>
          <FormContentStyles.BackgroundContainer>
            <RenderFormContent formDisabled={formDisabled} />
          </FormContentStyles.BackgroundContainer>
        </StyledModal>
      </ErrorProvider>
    </FormProvider>
  );
}

export default function CompanyTierVerificationModal() {
  return (
    <Suspense fallback={<LoadingModal />}>
      <ModalComponent />
    </Suspense>
  );
}
